import React from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { trackInstallClick } from "../lib/analytics";

/**
 * InstallBadgesRow — reusable Android + iPhone install CTA badges.
 *
 * Drops into Landing, Blog, Footer, etc. Each badge links to the platform-
 * forced route (/android or /iphone) so users land on the right install flow.
 *
 * Variants:
 *   - "default": full row with label and two badges
 *   - "compact": just the two badges, no label
 *
 * Props:
 *   variant      — "default" | "compact"
 *   align        — "left" | "center"
 *   utmSource    — appended as ?utm_source=... for attribution
 */
export default function InstallBadgesRow({
  variant = "default",
  align = "center",
  utmSource = "landing",
  className = "",
}) {
  const { language } = useApp();
  const es = language === "es";

  const utm = utmSource ? `?utm_source=${encodeURIComponent(utmSource)}` : "";
  const androidHref = `/android${utm}`;
  const iphoneHref = `/iphone${utm}`;

  const alignCls = align === "left" ? "items-start text-left" : "items-center text-center";
  const rowJustify = align === "left" ? "justify-start" : "justify-center";

  return (
    <div className={`flex flex-col ${alignCls} gap-4 ${className}`} data-testid="install-badges-row">
      {variant === "default" && (
        <div className={`font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 flex items-center gap-2 ${rowJustify} w-full`}>
          <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
          <span>{es ? "Disponible ahora — instala en tu teléfono" : "Available now — install on your phone"}</span>
        </div>
      )}
      <div className={`flex flex-wrap gap-3 ${rowJustify} w-full`}>
        <Link
          to={androidHref}
          className="block w-[180px] sm:w-[200px] transition-transform hover:scale-[1.04] active:scale-[0.98]"
          data-testid="install-badge-android"
          aria-label={es ? "Instalar en Android" : "Install on Android"}
          onClick={() => trackInstallClick("android", utmSource)}
        >
          <img
            src={es ? "/store-badges/style1-android-es.png" : "/store-badges/style1-android-en.png"}
            alt={es ? "Obtener para Android" : "Get it for Android"}
            className="w-full h-auto rounded-lg shadow-lg shadow-black/40"
            loading="lazy"
          />
        </Link>
        <Link
          to={iphoneHref}
          className="block w-[180px] sm:w-[200px] transition-transform hover:scale-[1.04] active:scale-[0.98]"
          data-testid="install-badge-iphone"
          aria-label={es ? "Instalar en iPhone" : "Install on iPhone"}
          onClick={() => trackInstallClick("ios", utmSource)}
        >
          <img
            src={es ? "/store-badges/style1-ios-es.png" : "/store-badges/style1-ios-en.png"}
            alt={es ? "Instalar en iPhone" : "Install on iPhone"}
            className="w-full h-auto rounded-lg shadow-lg shadow-black/40"
            loading="lazy"
          />
        </Link>
      </div>
      {variant === "default" && (
        <Link
          to="/install"
          className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/40 hover:text-white/70 transition"
          data-testid="install-badges-learn-more"
        >
          {es ? "¿Cómo se instala? →" : "How does it install? →"}
        </Link>
      )}
    </div>
  );
}
